import { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import { IPosOrder } from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableOrdersTotal,
} from '@modules/point-of-sale/slice/pos-selector';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Button,
  CardHeader,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAppSelector } from '@store/index';
import { formatNumber } from '@utils/format';
import { ReactNode, useEffect, useState } from 'react';

const POSSplitBillModal = ({
  modalDisclosure,
}: {
  modalDisclosure: UseDisclosureInterface;
}) => {
  // #region STATE
  const { toast } = useToast();
  const orders = useAppSelector(selectPOSTableOrders);
  const total = useAppSelector(selectPOSTableOrdersTotal);
  const tableDetails = useAppSelector(selectPOSTableDetails);

  const [checkedLeft, setCheckedLeft] = useState<IPosOrder[]>([]);
  const [checkedRight, setCheckedRight] = useState<IPosOrder[]>([]);
  const [left, setLeft] = useState<IPosOrder[]>([]);
  const [right, setRight] = useState<IPosOrder[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (orders.length <= 0 || !modalDisclosure.isOpen) return;
    setLeft(orders);
  }, [orders, modalDisclosure.isOpen]);
  // #endregion

  // #region FUNCTION

  const handleReset = () => {
    setCheckedLeft([]);
    setCheckedRight([]);
    setLeft(orders.length >= 0 ? orders : []);
    setRight([]);
    setSelectedQuantities({});
  };

  /** Toggle a single item (side specific) */
  const handleToggleLeft = (order: IPosOrder) => () => {
    const exists = checkedLeft.some((c) => c.orderID === order.orderID);
    if (exists) {
      setCheckedLeft(checkedLeft.filter((c) => c.orderID !== order.orderID));
    } else {
      setCheckedLeft([...checkedLeft, order]);
    }
  };

  const handleToggleRight = (order: IPosOrder) => () => {
    const exists = checkedRight.some((c) => c.orderID === order.orderID);
    if (exists) {
      setCheckedRight(checkedRight.filter((c) => c.orderID !== order.orderID));
    } else {
      setCheckedRight([...checkedRight, order]);
    }
  };

  /** Toggle all */
  const handleToggleAllLeft = (items: IPosOrder[]) => () => {
    if (checkedLeft.length === items.length) {
      setCheckedLeft([]);
    } else {
      setCheckedLeft(items);
    }
  };

  const handleToggleAllRight = (items: IPosOrder[]) => () => {
    if (checkedRight.length === items.length) {
      setCheckedRight([]);
    } else {
      setCheckedRight(items);
    }
  };

  /** Move from Left -> Right */
  const handleCheckedRight = () => {
    const updatedLeft: IPosOrder[] = [];
    const movedOrders: IPosOrder[] = [];

    left.forEach((order) => {
      if (checkedLeft.some((c) => c.orderID === order.orderID)) {
        const qty =
          selectedQuantities[order.orderID + 'left'] ?? order.quantity;

        if (qty < order.quantity) {
          updatedLeft.push({ ...order, quantity: order.quantity - qty });
          movedOrders.push({ ...order, quantity: qty });
        } else {
          movedOrders.push(order);
        }
      } else {
        updatedLeft.push(order);
      }
    });

    const updatedRight = [...right];
    movedOrders.forEach((moved) => {
      const existing = updatedRight.find((r) => r.orderID === moved.orderID);
      if (existing) {
        existing.quantity += moved.quantity;
      } else {
        updatedRight.push(moved);
      }
    });

    setLeft(updatedLeft);
    setRight(updatedRight);
    setCheckedLeft([]);
    setSelectedQuantities({});
  };

  /** Move from Right -> Left */
  const handleCheckedLeft = () => {
    const updatedRight: IPosOrder[] = [];
    const movedOrders: IPosOrder[] = [];

    right.forEach((order) => {
      if (checkedRight.some((c) => c.orderID === order.orderID)) {
        const qty =
          selectedQuantities[order.orderID + 'right'] ?? order.quantity;

        if (qty < order.quantity) {
          updatedRight.push({ ...order, quantity: order.quantity - qty });
          movedOrders.push({ ...order, quantity: qty });
        } else {
          movedOrders.push(order);
        }
      } else {
        updatedRight.push(order);
      }
    });

    const updatedLeft = [...left];
    movedOrders.forEach((moved) => {
      const existing = updatedLeft.find((l) => l.orderID === moved.orderID);
      if (existing) {
        existing.quantity += moved.quantity;
      } else {
        updatedLeft.push(moved);
      }
    });

    setRight(updatedRight);
    setLeft(updatedLeft);
    setCheckedRight([]);
    setSelectedQuantities({});
  };

  // #endregion

  // #region UI
  /** Custom list renderer */
  const customList = (
    title: ReactNode,
    items: IPosOrder[],
    checkedItems: IPosOrder[],
    handleToggle: (order: IPosOrder) => () => void,
    handleToggleAll: (items: IPosOrder[]) => () => void,
    side: 'left' | 'right',
  ) => {
    return (
      <Stack>
        <Stack direction='row'>
          <CardHeader
            sx={{ p: 0, pb: 1 }}
            avatar={
              <Checkbox
                onClick={handleToggleAll(items)}
                checked={
                  checkedItems.length === items.length && items.length !== 0
                }
                indeterminate={
                  checkedItems.length !== items.length &&
                  checkedItems.length !== 0
                }
                disabled={items.length === 0}
              />
            }
            title={title}
            subheader={`${checkedItems.length}/${items.length} selected`}
          />

          <ListItemText
            primary={`${formatNumber(total, 2, 2)}`}
            sx={{
              textAlign: 'right',
              '.MuiTypography-root': { fontWeight: '500!important' },
            }}
          />
        </Stack>
        <Divider />
        <List
          sx={{
            minWidth: { xs: '100%', sm: 400 },
            height: 350,
            maxHeight: { xs: '100%', sm: 500 },
            bgcolor: 'background.paper',
            overflow: 'auto',
            p: 0,
          }}
          dense
          component='div'
          role='list'
        >
          {items.map((order, i) => {
            const labelId = `transfer-list-item-${order.orderID}-${i}`;
            const quantity = order.quantity;
            const isChecked = checkedItems.some(
              (c) => c.orderID === order.orderID && c.quantity === quantity,
            );

            const rawOrder = orders?.find(
              (item) =>
                item.orderID === order.orderID && item.menuID === order.menuID,
            );
            const origPrice =
              (rawOrder && rawOrder?.netAmount / rawOrder?.quantity) ??
              order.netAmount;
            return (
              <ListItemButton
                key={order.orderID + i + quantity + side}
                role='listitem'
                onClick={handleToggle(order)}
                sx={{ p: 0 }}
                disableRipple
              >
                <ListItemIcon>
                  <Checkbox
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <Select
                  size='small'
                  value={
                    selectedQuantities[order.orderID + side] ?? order.quantity
                  }
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSelectedQuantities((prev) => ({
                      ...prev,
                      [order.orderID + side]: e.target.value as number,
                    }));
                  }}
                  sx={{ minWidth: 60, '.MuiSelect-select': { py: 0.5 }, mr: 1 }}
                >
                  {[...Array(order.quantity)].map((_, i) => (
                    <MenuItem
                      key={i + 1}
                      value={i + 1}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
                <ListItemText
                  id={labelId}
                  primary={`${order.description} (x${order.quantity})`}
                  secondary={`${formatNumber(origPrice, 2, 2)}`}
                />
                <ListItemText
                  primary={`${formatNumber(origPrice * order.quantity, 2, 2)}`}
                  sx={{
                    textAlign: 'right',
                    '.MuiTypography-root': { fontWeight: '500!important' },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Stack>
    );
  };
  // #endregion

  // #region HANDLER
  const handleClose = () => {
    modalDisclosure.onClose();
    setTimeout(() => {
      handleReset();
    }, 100);
  };

  const handleSave = async () => {
    toast({ message: 'No API Message' });
    handleClose();
  };
  // #endregion

  return (
    <ModalWrapper
      {...{
        modalDisclosure,
        withHeader: {
          title: 'Split Bill: ' + tableDetails?.tableAcr,
          onClose: handleClose,
        },
      }}
    >
      <Stack gap={3}>
        <Grid2 container spacing={2}>
          <Grid2>
            {customList(
              'Source Table',
              left,
              checkedLeft,
              handleToggleLeft,
              handleToggleAllLeft,
              'left',
            )}
          </Grid2>
          <Grid2
            sx={{
              display: 'flex',
              borderRight: '1px solid #EEE',
              borderLeft: '1px solid #EEE',
            }}
          >
            <Grid2
              container
              direction='column'
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 1,
              }}
            >
              <Button
                variant='outlined'
                onClick={handleCheckedRight}
                disabled={checkedLeft.length === 0}
              >
                <ChevronRight />
              </Button>
              <Button
                variant='outlined'
                onClick={handleCheckedLeft}
                disabled={checkedRight.length === 0}
              >
                <ChevronLeft />
              </Button>
            </Grid2>
          </Grid2>
          <Grid2>
            {customList(
              'Spawned Table',
              right,
              checkedRight,
              handleToggleRight,
              handleToggleAllRight,
              'right',
            )}
          </Grid2>
        </Grid2>

        <Stack
          direction='row'
          gap={2}
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: '#FFF',
            justifyContent: 'flex-end',
            borderTop: '1px solid #EEE',
            pt: 3,
          }}
        >
          <Button variant='text' onClick={handleReset}>
            Reset
          </Button>
          <Button variant='outlined' onClick={handleClose}>
            Close
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={right?.length <= 0}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </ModalWrapper>
  );
};

export default POSSplitBillModal;
